import { useMemo } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { GEFAHREN_QUESTIONS } from "./data";
import GefahrenControl, { isCascoSelector, isInfoQuestion } from "./GefahrenControl";
import type { GefahrenAnswers, GefahrenQuestion, KlasseKey, TarifierungKey } from "./types";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "20px",
  },
  notice: {
    fontSize: "0.85rem",
    color: BRAND.muted,
    fontStyle: "italic",
    backgroundColor: BRAND.bg,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "dashed", BRAND.borderDashed),
  },
  group: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    paddingTop: "18px",
    paddingBottom: "18px",
    paddingLeft: "20px",
    paddingRight: "20px",
    display: "flex",
    flexDirection: "column",
    rowGap: "14px",
  },
  groupHeader: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
  },
  groupIcon: {
    width: "44px",
    height: "44px",
    flexShrink: 0,
    ...shorthands.borderRadius("999px"),
    color: BRAND.white,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  groupTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.2rem",
    color: BRAND.ink,
    margin: 0,
  },
  groupSub: {
    fontSize: "0.78rem",
    color: BRAND.muted,
  },
  rowList: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  questionRow: {
    display: "grid",
    gridTemplateColumns: "1fr",
    columnGap: "16px",
    rowGap: "6px",
    paddingTop: "10px",
    paddingBottom: "10px",
    ...shorthands.borderTop("1px", "dashed", BRAND.borderDashed),
    "@media (min-width: 720px)": {
      gridTemplateColumns: "minmax(220px, 0.9fr) 1.1fr",
      alignItems: "center",
    },
  },
  questionRowFirst: {
    ...shorthands.borderTop("0"),
    paddingTop: "0",
  },
  questionLabel: {
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
  },
  questionFrage: {
    fontSize: "0.95rem",
    color: BRAND.ink,
    fontWeight: 600,
  },
  questionMerkmal: {
    fontSize: "0.78rem",
    color: BRAND.muted,
    fontStyle: "italic",
  },
  control: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "9px",
    paddingBottom: "9px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    outlineWidth: 0,
    transitionProperty: "border-color, box-shadow",
    transitionDuration: "120ms",
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      boxShadow: `0 0 0 3px ${BRAND.purple}33`,
    },
  },
  toggleRow: {
    display: "inline-flex",
    columnGap: "8px",
  },
  toggleBtn: {
    cursor: "pointer",
    appearance: "none",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
    fontWeight: 600,
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "16px",
    paddingRight: "16px",
    minWidth: "76px",
    ...shorthands.borderRadius("999px"),
    ...shorthands.border("1.5px", "solid", BRAND.border),
    transitionProperty: "background-color, color, border-color",
    transitionDuration: "120ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
    },
  },
  toggleBtnYes: {
    backgroundColor: BRAND.pillarGreen,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.pillarGreen),
  },
  toggleBtnNo: {
    backgroundColor: "#a1331c",
    color: BRAND.white,
    ...shorthands.borderColor("#a1331c"),
  },
  infobox: {
    backgroundColor: "#fff5e6",
    color: "#7a4a10",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    fontSize: "0.88rem",
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "solid", "#f3d4a3"),
    display: "flex",
    alignItems: "flex-start",
    columnGap: "10px",
  },
  infoIcon: {
    color: BRAND.gold,
    flexShrink: 0,
    marginTop: "2px",
  },
  cascoTiles: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "10px",
    "@media (min-width: 540px)": {
      gridTemplateColumns: "repeat(3, 1fr)",
    },
  },
  cascoTile: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    fontWeight: 600,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    rowGap: "6px",
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "12px",
    paddingRight: "12px",
    minHeight: "100px",
    textAlign: "center",
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1.5px", "solid", BRAND.border),
    transitionProperty: "border-color, background-color, transform",
    transitionDuration: "140ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      transform: "translateY(-1px)",
    },
  },
  cascoTileActive: {
    backgroundColor: "#f5eef6",
    boxShadow: "0 0 0 3px rgba(77,32,81,0.18)",
    ...shorthands.borderColor(BRAND.purple),
  },
  cascoIcon: {
    width: "36px",
    height: "36px",
    color: BRAND.purple,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
});

const SECTION_DEFS: Record<string, { title: string; subtitle: string; color: string; icon: React.ReactNode }> = {
  "1. KFZ-Haftpflichtversicherung": {
    title: "KFZ-Haftpflichtversicherung",
    subtitle: "Pflichtdeckung für Schäden an Dritten",
    color: "#a1331c",
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 2l8 3v5c0 5-3 8-8 10-5-2-8-5-8-10V5z" />
        <path d="M8 11l2 2 4-4" />
      </svg>
    ),
  },
  "2. KFZ-Kaskoversicherung": {
    title: "KFZ-Kaskoversicherung",
    subtitle: "Schutz für eigenes Fahrzeug",
    color: BRAND.pillarBlue,
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 13l1.5-5a2 2 0 0 1 1.9-1.4h9.2a2 2 0 0 1 1.9 1.4L19 13" />
        <path d="M2 13h18v4a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1v-1H6v1a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z" />
        <circle cx="6.5" cy="16" r="1.4" />
        <circle cx="15.5" cy="16" r="1.4" />
      </svg>
    ),
  },
  insa: {
    title: "Insassen- & Zusatzbausteine",
    subtitle: "Insassenunfall, Schutzbrief, Rabattschutz, …",
    color: BRAND.gold,
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="7" cy="8" r="2.6" />
        <circle cx="15" cy="8" r="2.6" />
        <path d="M2 18c0-2.6 2.4-4.4 5-4.4S12 15.4 12 18" />
        <path d="M10 18c0-2.6 2.4-4.4 5-4.4S20 15.4 20 18" />
      </svg>
    ),
  },
  "4. Vertragsdaten und Bankverbindung": {
    title: "Vertragsdaten & Bankverbindung",
    subtitle: "Beginn, Ablauf, Zahlweise, IBAN",
    color: BRAND.purple,
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="16" height="14" rx="2" />
        <path d="M3 9h16" />
        <path d="M7 13h4" />
      </svg>
    ),
  },
  // "5. Vorversicherung" wird bewusst nicht in der Gefahren-Stufe gezeigt:
  // Vorversicherer und Vorvertragsnummer werden bereits im Schritt
  // „Vertragsbasis" beim Vorvertrag „Ja" erfasst.
};

const SECTION_ORDER = [
  "1. KFZ-Haftpflichtversicherung",
  "2. KFZ-Kaskoversicherung",
  "insa",
];

// Section "4. Vertragsdaten und Bankverbindung" lebt jetzt im eigenen
// Zahlung-Step und wird hier nicht mehr gezeigt.
const ZAHLUNG_SECTION = "4. Vertragsdaten und Bankverbindung";

/**
 * Common Vertragsdaten-/Zahlungs-Felder die NICHT je Tarif × Klasse im Schema
 * dupliziert werden — sie werden zusätzlich zu den schema-getriebenen Fragen
 * in jedem Zahlung-Step gerendert.
 *
 * Die Prämien-/Beitragsfelder werden bewusst zuerst gerendert, damit
 * Jahresprämie & Beitragsinformationen oben in der Übersicht stehen.
 */
const COMMON_PREMIUM_QUESTIONS: GefahrenQuestion[] = [
  { key: "common.jahrespraemie_brutto", section: ZAHLUNG_SECTION, frage: "Jahresprämie (Brutto)", merkmal: "€" },
  { key: "common.jahrespraemie_netto", section: ZAHLUNG_SECTION, frage: "Jahresprämie (Netto)", merkmal: "€" },
  { key: "common.versicherungssteuersatz", section: ZAHLUNG_SECTION, frage: "Versicherungssteuersatz", merkmal: "%" },
  { key: "common.praemie_je_zahlweise_brutto", section: ZAHLUNG_SECTION, frage: "Prämie je Zahlweise (Brutto)", merkmal: "€" },
];

const COMMON_VERTRAGSDATEN_QUESTIONS: GefahrenQuestion[] = [
  { key: "common.hauptfaelligkeit", section: ZAHLUNG_SECTION, frage: "Hauptfälligkeitsdatum", merkmal: "Vorbelegung 01.01., alternativ manuell" },
  { key: "common.naechste_faelligkeit", section: ZAHLUNG_SECTION, frage: "Nächste Fälligkeit", merkmal: "Datum" },
  { key: "common.abweichender_rechnungsempfaenger", section: ZAHLUNG_SECTION, frage: "Abweichender Rechnungsempfänger", merkmal: "Name und Anschrift (falls abweichend)" },
];

// Bankverbindung wird nicht mehr als Freitext geführt, sondern in IBAN/BIC/
// Bankname/Kontoinhaber aufgesplittet. Die Felder werden tarif-/klassen-
// unabhängig in jedem Zahlung-Step gerendert; die alten "Bankverbindung"-
// Zeilen aus dem Schema werden in filterZahlungQuestions herausgefiltert.
const COMMON_BANKVERBINDUNG_QUESTIONS: GefahrenQuestion[] = [
  { key: "common.bank_iban", section: ZAHLUNG_SECTION, frage: "IBAN", merkmal: "IBAN" },
  { key: "common.bank_bic", section: ZAHLUNG_SECTION, frage: "BIC", merkmal: "BIC" },
  { key: "common.bank_name", section: ZAHLUNG_SECTION, frage: "Name der Bank", merkmal: "Bankname" },
  { key: "common.bank_kontoinhaber", section: ZAHLUNG_SECTION, frage: "Kontoinhaber", merkmal: "Name" },
];

export const filterUmfangQuestions = (questions: GefahrenQuestion[]) =>
  questions.filter((q) => q.section !== ZAHLUNG_SECTION);
export const filterZahlungQuestions = (questions: GefahrenQuestion[]) => {
  const schemaDriven = questions
    .filter((q) => q.section === ZAHLUNG_SECTION)
    .filter((q) => q.frage.toLowerCase() !== "bankverbindung");
  return [
    ...COMMON_PREMIUM_QUESTIONS,
    ...schemaDriven,
    ...COMMON_BANKVERBINDUNG_QUESTIONS,
    ...COMMON_VERTRAGSDATEN_QUESTIONS,
  ];
};

const groupBySection = (questions: GefahrenQuestion[]) => {
  const map: Record<string, GefahrenQuestion[]> = {};
  for (const q of questions) {
    (map[q.section] ??= []).push(q);
  }
  return SECTION_ORDER.filter((s) => map[s] && map[s].length > 0).map((s) => ({
    section: s,
    questions: map[s],
  }));
};

const isInfo = isInfoQuestion;


type Props = {
  tarifierung: TarifierungKey | "";
  klasse: KlasseKey | null;
  answers: GefahrenAnswers;
  onChange: (key: string, value: string) => void;
};

const GefahrenSection = ({ tarifierung, klasse, answers, onChange }: Props) => {
  const styles = useStyles();
  const groups = useMemo(() => {
    if (!tarifierung || !klasse) return [];
    const list = GEFAHREN_QUESTIONS[tarifierung]?.[klasse] ?? [];
    return groupBySection(list);
  }, [tarifierung, klasse]);

  if (!tarifierung || !klasse) {
    return (
      <div className={styles.notice}>
        Bitte zuerst die Tarifierungsart wählen — die Gefahrenliste hängt von Tarif und Fahrzeugklasse ab.
      </div>
    );
  }

  // Special handling: collapse 3 "Auswahl Kaskoversicherung" rows into a single tile picker.
  // Records the chosen key under a synthetic key per group so we can render in summary.
  const renderCascoSelector = (questions: GefahrenQuestion[]) => {
    const cascoQs = questions.filter((q) => isCascoSelector(q.frage));
    if (!cascoQs.length) return null;
    const synthKey = `${cascoQs[0].key}__cascoChoice`;
    const value = answers[synthKey] ?? "";
    const tiles = [
      { key: "voll_inkl_teil", label: "Voll- inkl. Teilkasko", icon: (
        <svg viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2l10 4v6c0 6-4 10-10 12-6-2-10-6-10-12V6z" />
          <path d="M9 13l3 3 6-6" />
        </svg>
      ) },
      { key: "teil", label: "Teilkasko", icon: (
        <svg viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2l10 4v6c0 6-4 10-10 12-6-2-10-6-10-12V6z" />
          <path d="M14 8v8" />
        </svg>
      ) },
      { key: "keine", label: "Keine Kasko", icon: (
        <svg viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2l10 4v6c0 6-4 10-10 12-6-2-10-6-10-12V6z" />
          <path d="M9 9l10 10M19 9L9 19" />
        </svg>
      ) },
    ];
    return (
      <div className={mergeClasses(styles.questionRow, styles.questionRowFirst)}>
        <div className={styles.questionLabel}>
          <span className={styles.questionFrage}>Auswahl Kaskoversicherung</span>
          <span className={styles.questionMerkmal}>Wählen Sie eine Variante</span>
        </div>
        <div className={styles.cascoTiles}>
          {tiles.map((t) => {
            const active = value === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => onChange(synthKey, active ? "" : t.key)}
                className={mergeClasses(styles.cascoTile, active && styles.cascoTileActive)}
                aria-pressed={active}
              >
                <span className={styles.cascoIcon}>{t.icon}</span>
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Kasko-Auswahl wird per Tile-Picker erfasst und unter einem synthetischen
  // Key gespeichert. Hier global (über Gruppen hinweg) auflösen, damit andere
  // Gruppen (Insa: Rabattschutz Kasko) den Wert sehen.
  const globalCascoValue: string = (() => {
    for (const g of groups) {
      const cascoQs = g.questions.filter((q) => isCascoSelector(q.frage));
      if (!cascoQs.length) continue;
      const synthKey = `${cascoQs[0].key}__cascoChoice`;
      return answers[synthKey] ?? "";
    }
    return "";
  })();

  // Insassenunfall innerhalb der "insa"-Gruppe (Ja/Nein) — steuert die
  // Sichtbarkeit der Folge-Felder Invalidität/Todesfall/Krankentagegeld/Heilkosten.
  const insaUnfallAntwort: string = (() => {
    const insaGroup = groups.find((g) => g.section === "insa");
    if (!insaGroup) return "";
    const q = insaGroup.questions.find((qq) =>
      qq.frage.toLowerCase().startsWith("insassenunfallversicherung"),
    );
    return q?.key ? answers[q.key] ?? "" : "";
  })();

  const INSA_FOLLOWUP_FRAGEN = new Set([
    "invalidität",
    "todesfall",
    "krankentagegeld",
    "heilkosten",
  ]);

  return (
    <div className={styles.root}>
      {groups.map((g) => {
        const def = SECTION_DEFS[g.section] ?? {
          title: g.section,
          subtitle: "",
          color: BRAND.purple,
          icon: null,
        };
        const isKasko = g.section === "2. KFZ-Kaskoversicherung";
        const isInsa = g.section === "insa";
        // questions to render row-by-row, excluding casco selector if present
        let rowQuestions = isKasko
          ? g.questions.filter((q) => !isCascoSelector(q.frage))
          : g.questions;

        // "falls ja, Versicherungssumme für" ist veraltet — die Versicherungssumme
        // wird je Folgefeld direkt erfasst.
        rowQuestions = rowQuestions.filter(
          (q) => q.frage.toLowerCase() !== "falls ja, versicherungssumme für",
        );

        // Conditional SB dropdowns: only show the matching dropdown for the
        // currently selected Kasko-Variante.
        if (isKasko) {
          const cascoQs = g.questions.filter((q) => isCascoSelector(q.frage));
          const synthKey = cascoQs[0]?.key
            ? `${cascoQs[0].key}__cascoChoice`
            : null;
          const cascoValue = synthKey ? answers[synthKey] ?? "" : "";
          rowQuestions = rowQuestions.filter((q) => {
            const lower = q.frage.toLowerCase();
            const isVollSb = lower.startsWith("selbstbeteiligung voll");
            const isTeilSb = lower.startsWith("selbstbeteiligung teilkasko");
            if (isVollSb) return cascoValue === "voll_inkl_teil";
            if (isTeilSb) return cascoValue === "teil";
            return true;
          });
        }

        // Bei "insa": Folgefelder der Insassenunfallversicherung nur zeigen,
        // wenn diese mit "ja" beantwortet wurde. Rabattschutz-Kasko nur
        // anzeigen, wenn überhaupt eine Kasko gewählt wurde (≠ "keine"/"").
        if (isInsa) {
          rowQuestions = rowQuestions.filter((q) => {
            const lower = q.frage.toLowerCase().trim();
            if (INSA_FOLLOWUP_FRAGEN.has(lower)) {
              return insaUnfallAntwort === "ja";
            }
            if (lower.startsWith("rabattschutz kfz-kaskoversicherung")) {
              return globalCascoValue === "voll_inkl_teil" || globalCascoValue === "teil";
            }
            return true;
          });
        }
        return (
          <div key={g.section} className={styles.group}>
            <div className={styles.groupHeader}>
              <span className={styles.groupIcon} style={{ backgroundColor: def.color }}>
                {def.icon}
              </span>
              <div>
                <h3 className={styles.groupTitle}>{def.title}</h3>
                <span className={styles.groupSub}>{def.subtitle}</span>
              </div>
            </div>
            {isKasko && renderCascoSelector(g.questions)}
            <div className={styles.rowList}>
              {rowQuestions.map((q, idx) => (
                <div
                  key={q.key}
                  className={mergeClasses(styles.questionRow, (idx === 0 && !(isKasko)) && styles.questionRowFirst)}
                >
                  <div className={styles.questionLabel}>
                    <span className={styles.questionFrage}>{q.frage}</span>
                    {q.merkmal && !isInfo(q.frage) && (
                      <span className={styles.questionMerkmal}>{q.merkmal}</span>
                    )}
                  </div>
                  <div className={styles.control}>
                    <GefahrenControl
                      question={q}
                      value={answers[q.key!] ?? ""}
                      onChange={onChange}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default GefahrenSection;
